"use client";

import "@/components/feature/masonry.css";
import {
  CURVED_ROW_MAX_ITEMS,
  type CurvedRowDirection,
  curvedRowDesktopTrackTemplate,
  curvedRowMobileCellClass,
  curvedRowTransform,
  isCurvedRowFeaturedIndex,
} from "@/lib/curved-row-layout";
import { sidePaddingContentColumns } from "@/lib/game-sections";
import { cn } from "@/lib/utils";
import type { CSSProperties, ReactNode } from "react";

export interface CurvedRowListProps<T> {
  items: T[];
  direction?: CurvedRowDirection;
  enlargeMiddle?: boolean;
  /** Columns of inset on each side of a 6-column frame. 0.5 removes one column. */
  sidePadding?: number;
  getKey: (item: T, index: number) => string;
  renderItem: (item: T, index: number, featured: boolean) => ReactNode;
  className?: string;
  role?: string;
  "aria-label"?: string;
  "data-testid"?: string;
}

/**
 * Shared packing for public curved rows and the admin preview: mobile 6-col
 * rearrange, desktop arc/crown. Extra tiles beyond 6 are ignored.
 */
export function CurvedRowList<T>({
  items,
  direction = "up",
  enlargeMiddle = true,
  sidePadding = 0,
  getKey,
  renderItem,
  className,
  role,
  "aria-label": ariaLabel,
  "data-testid": testId = "game-section-curved-row",
}: CurvedRowListProps<T>) {
  const visible = items.slice(0, CURVED_ROW_MAX_ITEMS);
  const count = visible.length;
  if (count === 0) return null;

  const originClass = direction === "down" ? "origin-top" : "origin-bottom";
  const desktopPadding = direction === "up" ? "sm:pt-10 sm:pb-6" : "sm:pt-1 sm:pb-12";
  const desktopColumns = curvedRowDesktopTrackTemplate(count, enlargeMiddle).replaceAll("_", " ");
  const contentColumns = sidePaddingContentColumns(sidePadding);

  return (
    <ul
      role={role}
      aria-label={ariaLabel}
      className={cn(
        "grid w-full grid-cols-6 items-end justify-center gap-x-4 gap-y-5 px-2 pt-2 pb-2",
        "sm:mx-auto sm:w-[94%] sm:grid-flow-col sm:items-end sm:px-0 sm:gap-x-5 sm:gap-y-0",
        sidePadding > 0 && "curved-row-padded",
        "curved-row-public",
        desktopPadding,
        className
      )}
      style={
        {
          ["--curved-row-columns" as string]: desktopColumns,
          ...(sidePadding > 0
            ? { ["--section-content-columns" as string]: String(contentColumns) }
            : {}),
        } as CSSProperties
      }
      data-testid={testId}
      data-curve-direction={direction}
      data-enlarge-middle={enlargeMiddle ? "true" : "false"}
      data-side-padding={String(sidePadding)}
    >
      {visible.map((item, index) => {
        const featured = enlargeMiddle && isCurvedRowFeaturedIndex(index, count);
        const transform = curvedRowTransform(index, count, direction);
        return (
          <li
            key={getKey(item, index)}
            className={cn(
              "min-w-0 sm:col-span-1 sm:col-start-auto sm:row-auto sm:row-start-auto",
              curvedRowMobileCellClass(index, count)
            )}
          >
            <div
              className={cn(
                "curved-row-tile block max-sm:transform-none sm:[transform:var(--curved-row-transform)]",
                originClass
              )}
              style={{ ["--curved-row-transform" as string]: transform } as CSSProperties}
              data-featured={featured ? "true" : undefined}
            >
              {renderItem(item, index, featured)}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
